import { JiraAssignedTicket } from '@Models/types';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import { useAuth } from './CognitoAuthProvider';
import { jiraAPI } from '@Lib/axios/jira';
import showToast from '@Common/toast/showToast';
import { isAxiosError } from 'axios';

interface TicketContextProps {
  tickets: JiraAssignedTicket[];
  fetchTickets: (options?: { force?: boolean }) => Promise<void>;
  selectedTicket: JiraAssignedTicket | null;
  setSelectedTicket: (ticket: JiraAssignedTicket) => void;
  selectTicket: (id: string | number) => Promise<void>;
  isLoading: boolean;
  isLoadingDetail: boolean;
  error: string | null;
  detailError: string | null;
  clearDetailError: () => void;
}

const TicketContext = createContext<TicketContextProps | undefined>(undefined);

export const TicketContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [tickets, setTickets] = useState<JiraAssignedTicket[]>([]);
  const [selectedTicket, setSelectedTicket] =
    useState<JiraAssignedTicket | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  const { logout } = useAuth();
  const jira = useMemo(() => jiraAPI(logout), [logout]);

  const fetchTickets = useCallback(
    async (options?: { force?: boolean }) => {
      const hasData = tickets.length > 0;

      if (hasData && !options?.force) return;
      if (error && !options?.force) return;

      setIsLoading(true);
      setError(null);

      try {
        const result = await jira.getAssignedRecords();
        setTickets(result);
      } catch (e) {
        console.error('Critical fetch error', e);
        setError('Failed to load Jira tickets');
      } finally {
        setIsLoading(false);
      }
    },
    [tickets, error, jira]
  );

  const selectTicket = useCallback(
    async (id: string | number) => {
      const ticket = tickets.find((t) => t.id === id);
      if (!ticket) return;

      // Highlight the card immediately
      setSelectedTicket(ticket);
      setDetailError(null);

      // Detail already cached — nothing to fetch
      if (ticket.isDetailLoaded) return;

      // Abort any in-flight detail request
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoadingDetail(true);

      try {
        const detail = await jira.getAssignedTicketDetails(
          ticket.reference_num,
          controller.signal
        );

        // Discard result if a newer request has already taken over
        if (controller.signal.aborted) return;

        const enriched: JiraAssignedTicket = {
          ...ticket,
          ...detail,
          isDetailLoaded: true,
        };

        setTickets((prev) => prev.map((t) => (t.id === id ? enriched : t)));
        setSelectedTicket(enriched);
      } catch (e) {
        // Cancelled by AbortController — not an error
        if (
          (e instanceof Error && e.name === 'AbortError') ||
          (e instanceof Error && e.message === 'AbortError') ||
          (isAxiosError(e) && e.message === 'AbortError')
        ) {
          return;
        }

        console.error('Failed to load ticket detail', e);
        const message =
          e instanceof Error ? e.message : 'Failed to load ticket details';
        setDetailError(message);
        showToast.error('Failed to load ticket details');
      } finally {
        if (!controller.signal.aborted) {
          setIsLoadingDetail(false);
        }
      }
    },
    [tickets, jira]
  );

  const clearDetailError = useCallback(() => setDetailError(null), []);

  useEffect(() => {
    if (selectedTicket && tickets.length > 0) {
      const updatedTicket = tickets.find(
        (ticket) => ticket.reference_num === selectedTicket.reference_num
      );
      if (
        updatedTicket &&
        updatedTicket.workflow_status?.color !==
          selectedTicket.workflow_status?.color
      ) {
        setSelectedTicket({ ...updatedTicket });
      }
    }
  }, [tickets, selectedTicket]);

  return (
    <TicketContext.Provider
      value={{
        tickets,
        fetchTickets,
        selectedTicket,
        setSelectedTicket,
        selectTicket,
        isLoading,
        isLoadingDetail,
        error,
        detailError,
        clearDetailError,
      }}
    >
      {children}
    </TicketContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useTicketContext = (): TicketContextProps => {
  const context = useContext(TicketContext);
  if (!context)
    throw new Error(
      'useTicketContext must be used within a TicketContextProvider'
    );
  return context;
};
